var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable @typescript-eslint/naming-convention */
// Default-exported from index.tsx rather than a same-named sibling: webpack's require.context
// mount resolution self-imports when the component file shares the directory name.
import React, { useEffect, useState } from 'react';
import { Button, showErrorToast } from '@ui';
import { translate } from '@utils/i18n';
import api from '@utils/axios';
import { updateEndDateBuildingInspectionSeriesPath } from '@routes';
import EditEndDateModalView from './EditEndDateModalView';
import useCancelCountPreview from './useCancelCountPreview';
import { toIsoDate, isShortening } from './dateHelpers';
const namespace = 'inspection_series.edit_end_date_modal';
const ERB_TRIGGER_ATTRIBUTE = 'data-edit-expiration-trigger';
const AUTO_OPEN_PARAM = 'edit_expiration';
export default function EditEndDateModal({ inspectionSeriesId, buildingId, currentEndDate, minEndDate, maxEndDate, autoOpen = false, renderTrigger = true, onClose, onSaved }) {
    const initialDate = currentEndDate ? new Date(`${currentEndDate}T00:00:00`) : null;
    const earliestAllowedDate = new Date(`${minEndDate}T00:00:00`);
    const latestAllowedDate = new Date(`${maxEndDate}T00:00:00`);
    const [isOpen, setIsOpen] = useState(autoOpen);
    const [endDate, setEndDate] = useState(initialDate);
    const [isSaving, setIsSaving] = useState(false);
    // Only a shortening cancels anything, so only a shortening is previewed. An unchanged or later
    // date hits no endpoint and shows no notice.
    const shortening = isOpen && isShortening(endDate, initialDate);
    const countState = useCancelCountPreview(inspectionSeriesId, shortening ? endDate : null);
    // The deep link has done its job once the modal is open. Left in place it would reopen the modal
    // on every reload, and turn any copied URL into one that springs an editor on whoever opens it.
    // replaceState rather than pushState: this is not a navigation the back button should replay.
    useEffect(() => {
        if (!autoOpen)
            return;
        const url = new URL(window.location.href);
        if (!url.searchParams.has(AUTO_OPEN_PARAM))
            return;
        url.searchParams.delete(AUTO_OPEN_PARAM);
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }, [autoOpen]);
    // The legacy ERB show page supplies its own triggers — the Status dropdown item and the "(Edit)"
    // link beside the value — so they read like their neighbours. They open this island rather than
    // each mounting one.
    useEffect(() => {
        const triggers = Array.from(document.querySelectorAll(`[${ERB_TRIGGER_ATTRIBUTE}]`));
        if (triggers.length === 0)
            return undefined;
        const open = (event) => {
            event.preventDefault();
            setIsOpen(true);
        };
        triggers.forEach((trigger) => trigger.addEventListener('click', open));
        return () => triggers.forEach((trigger) => trigger.removeEventListener('click', open));
    }, []);
    const handleClose = () => {
        setEndDate(initialDate);
        setIsOpen(false);
        onClose === null || onClose === void 0 ? void 0 : onClose();
    };
    const handleSave = () => __awaiter(this, void 0, void 0, function* () {
        if (!endDate)
            return;
        setIsSaving(true);
        try {
            // `reload` tells the server who renders the confirmation. A reloading caller needs it in the
            // flash to survive the GET; a caller that stays put would otherwise leave that flash in the
            // session to surface later on an unrelated page.
            const { data } = yield api.patch(updateEndDateBuildingInspectionSeriesPath(buildingId, inspectionSeriesId), { inspection_series: { end_date: toIsoDate(endDate) }, reload: !onSaved });
            if (onSaved) {
                setIsSaving(false);
                setIsOpen(false);
                onSaved(data.message);
                return;
            }
            // The server set a flash notice; the reloaded page renders it. Toasting here would only
            // create a message the reload destroys before anyone reads it.
            window.location.reload();
        }
        catch (_a) {
            setIsSaving(false);
            showErrorToast(translate('save_failed', { namespace }));
        }
    });
    return (React.createElement(React.Fragment, null,
        renderTrigger && (React.createElement(Button, { variant: "default", onClick: () => setIsOpen(true) },
            React.createElement("i", { className: "fa-light fa-calendar-pen" }),
            " ",
            translate('trigger', { namespace }))),
        React.createElement(EditEndDateModalView, { isOpen: isOpen, endDate: endDate, originalEndDate: initialDate, minEndDate: earliestAllowedDate, maxEndDate: latestAllowedDate, countState: countState, isSaving: isSaving, onEndDateChange: setEndDate, onSave: () => {
                void handleSave();
            }, onClose: handleClose })));
}
